import React, { createContext, useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { sdk } from './sdk'
import { useAuth } from './AuthContext'
import { useCommunity } from './CommunityContext'
import { type Section } from '@/types/sections'
import { type Space } from '@/types/spaces'
import { isOpenSpace } from '@/types/access'
import { useRealtime, useRealtimeEvents } from './realtime/RealtimeProvider'

interface MenuContextType {
  sections: Section[]
  isLoading: boolean
  error: string | null
  refresh: (options?: { silent?: boolean }) => Promise<void>
  /** While true (a drag in the menu), silent refreshes wait and run once on release. */
  holdLive: (hold: boolean) => void
  updateSections: (sections: Section[]) => void
  getSpacesBySection: (sectionId: string) => Space[]
  getHomeSection: () => Section | null
  /** The first space this person can open (F-161a: never a padlocked one), home section first. */
  getHomeSpace: () => Space | null
  /**
   * The menu without the padlocked spaces (F-161a criterion 13). Anything that
   * picks a space on its own — the home, "the first space of", a selector —
   * reads this, so a member without a plan never lands on an offer they did
   * not click. The menu itself draws `sections`, padlocks included.
   */
  openSections: Section[]
}

const MenuContext = createContext<MenuContextType | undefined>(undefined)

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { community, isCommunityFound } = useCommunity()
  const [sections, setSections] = useState<Section[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const holdRef = React.useRef(false)
  const pendingRef = React.useRef(false)

  /* `silent`: a live update (another admin changed the menu). No spinner, no error
     state, and the same array when nothing changed, so nothing re-renders. */
  const fetchMenuData = React.useCallback(async (silent = false) => {
    if (!community?.id) return
    if (silent && holdRef.current) {
      pendingRef.current = true
      return
    }

    try {
      if (!silent) {
        setIsLoading(true)
        setError(null)
      }

      // Fetch sections - API handles visibility based on authentication
      const data = await sdk.sections.list()
      console.log('📋 [MenuContext] sections.list returned:', Array.isArray(data) ? data.length + ' sections' : typeof data)

      if (data) {
        if (silent) {
          setSections((previous) => (JSON.stringify(previous) === JSON.stringify(data) ? previous : data))
          setError(null)
        } else {
          setSections(data)
        }
      } else {
        throw new Error('Failed to fetch sections data')
      }
    } catch (err) {
      const e = err as { message?: string; response?: { status?: number; data?: { message?: string } } } | null
      console.error('❌ [MenuContext] failed to load the menu:', e?.response?.status, e?.response?.data?.message || e?.message)
      /* A failed live update keeps the menu on screen; the next notice tries again. */
      if (silent) return
      setError(e?.response?.data?.message || e?.message || 'Failed to load the menu')
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [community?.id])

  const refresh = React.useCallback(async (options: { silent?: boolean } = {}) => {
    await fetchMenuData(Boolean(options.silent))
  }, [fetchMenuData])

  const holdLive = React.useCallback((hold: boolean) => {
    holdRef.current = hold
    if (!hold && pendingRef.current) {
      pendingRef.current = false
      fetchMenuData(true)
    }
  }, [fetchMenuData])

  const updateSections = React.useCallback((newSections: Section[]) => {
    setSections(newSections)
  }, [])

  const getSpacesBySection = React.useCallback((sectionId: string): Space[] => {
    const section = sections.find(s => s.id === sectionId)
    if (!section || !section.spaces) return []

    // API already filters spaces based on authentication
    const visibleSpaces = section.spaces

    return visibleSpaces.sort((a, b) => a.order - b.order)
  }, [sections])

  const getHomeSection = React.useCallback((): Section | null => {
    return sections.find(section => section.isHome) || null
  }, [sections])

  const sortedSections = React.useMemo(() => {
    return [...sections].sort((a, b) => Number(!!b.isHome) - Number(!!a.isHome) || a.order - b.order)
  }, [sections])

  const openSections = React.useMemo(() => sortedSections.map((section) => (
    (section.spaces || []).some((space) => !isOpenSpace(space))
      ? { ...section, spaces: (section.spaces || []).filter(isOpenSpace) }
      : section
  )), [sortedSections])

  /* The home section's first open space; when all of it is padlocked, the first
     open space anywhere (home first, then the menu's order). No home section, no home space. */
  const getHomeSpace = React.useCallback((): Space | null => {
    if (!openSections.some((section) => section.isHome)) return null
    for (const section of openSections) {
      const first = [...(section.spaces || [])].sort((a, b) => a.order - b.order)[0]
      if (first) return first
    }
    return null
  }, [openSections])

  useEffect(() => {
    if (community?.id) {
      fetchMenuData()
    } else if (!isCommunityFound) {
      setIsLoading(false)
    }
  }, [community?.id, isCommunityFound, isAuthenticated, fetchMenuData])

  /* F-252: the menu depends on the language the app is in (sections and spaces
     marked for other languages are left out by the API). A switch in the header
     or in the profile refetches it quietly, without reloading the page. */
  const { i18n } = useTranslation()
  const language = i18n.language
  const languageRef = React.useRef(language)
  useEffect(() => {
    if (languageRef.current === language) return
    languageRef.current = language
    if (community?.id) void fetchMenuData(true)
  }, [language, community?.id, fetchMenuData])

  const value = React.useMemo(() => ({
    sections: sortedSections,
    isLoading,
    error,
    refresh,
    holdLive,
    updateSections,
    getSpacesBySection,
    getHomeSection,
    getHomeSpace,
    openSections
  }), [sortedSections, isLoading, error, refresh, holdLive, updateSections, getSpacesBySection, getHomeSection, getHomeSpace, openSections])

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  )
}

export function useMenu() {
  const context = useContext(MenuContext)
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider')
  }
  return context
}

/**
 * The live menu: refetches it silently on `menu.changed` (sections or spaces created, edited,
 * deleted, reordered, moved, access changed) and on `access.changed` (this person's own access).
 * Rendered inside the RealtimeProvider, which sits below the MenuProvider.
 */
export function MenuRealtimeSync() {
  const { refresh } = useMenu()
  const refetch = React.useCallback(() => { refresh({ silent: true }) }, [refresh])
  useRealtime(['community'], refetch, { topics: ['menu.changed'] })
  useRealtimeEvents((event) => {
    if (event.type === 'access.changed') refetch()
  })
  return null
}
